import React, { Fragment, useMemo, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress,
    IconButton,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ThemedLink } from '../../../components/themed/ThemedComponents';

function parseListMetadata(raw) {
    if (raw == null) return {};
    if (typeof raw === 'object') return { ...raw };
    try {
        const o = JSON.parse(raw);
        return o && typeof o === 'object' ? { ...o } : {};
    } catch (e) {
        return {};
    }
}

function descendantListIdsForOrgKey(orgKey, allLists) {
    return (allLists || [])
        .filter((l) => {
            const m = parseListMetadata(l.metadata);
            return String(m.organization || '') === String(orgKey);
        })
        .map((l) => l.id);
}

function organizationGroupLabel(orgKey, allLists) {
    const sample = (allLists || []).find((l) => {
        const m = parseListMetadata(l.metadata);
        return String(m.organization || '') === String(orgKey);
    });
    if (sample) {
        const m = parseListMetadata(sample.metadata);
        if (m.organizationName) {
            return String(m.organizationName);
        }
    }
    const byExt = {};
    (allLists || []).forEach((l) => {
        if (l.external_id) {
            byExt[l.external_id] = l;
        }
    });
    const parent = byExt[orgKey];
    return (parent && parent.name) || String(orgKey);
}

function setBulkSelection(onUserListSelected, selectedLists, listIds, select) {
    listIds.forEach((id) => {
        const isOn = selectedLists.includes(id);
        if (select && !isOn) onUserListSelected(id);
        if (!select && isOn) onUserListSelected(id);
    });
}

function SelectUsersTable({
    lists,
    selectedLists,
    onUserListSelected,
    searchValue,
    ongoing,
    previousEmployees,
    requestUsersForList,
    loadingUsers,
    organizeByOrganization = false,
}) {
    const [recipientsShown, setRecipientsShown] = useState([]);
    const [expandedOrgIds, setExpandedOrgIds] = useState(() => new Set());

    const toggleExpanded = useCallback((listId) => {
        setExpandedOrgIds((prev) => {
            const next = new Set(prev);
            if (next.has(listId)) next.delete(listId);
            else next.add(listId);
            return next;
        });
    }, []);

    const toggleRecipients = (id, e) => {
        const recipients = recipientsShown.slice(0);

        if (recipientsShown.includes(id)) {
            setRecipientsShown(recipients.filter((x) => x !== id));
        } else {
            recipients.push(id);
            requestUsersForList(id);
        }
        setRecipientsShown(recipients);
        e.stopPropagation();
    };

    const q = (searchValue || '').toLowerCase();

    const searchedList = useMemo(() => {
        const base = (lists || [])
            .filter((list) =>
                (list.name || '').toLowerCase().includes(q)
            )
            .sort(
                (listA, listB) =>
                    new Date(listB.created) - new Date(listA.created)
            );
        return base;
    }, [lists, q]);

    const treeModel = useMemo(() => {
        if (!organizeByOrganization || !lists || lists.length === 0) {
            return null;
        }
        const all = lists;
        const orgKeys = new Set();
        all.forEach((l) => {
            const m = parseListMetadata(l.metadata);
            if (m.organization) {
                orgKeys.add(String(m.organization));
            }
        });
        const parents = Array.from(orgKeys)
            .map((key) => ({
                key,
                label: organizationGroupLabel(key, all),
            }))
            .sort((a, b) =>
                (a.label || '').localeCompare(b.label || '', undefined, {
                    sensitivity: 'base',
                })
            );

        const used = new Set();
        const childrenByOrgKey = new Map();
        parents.forEach(({ key }) => {
            const ch = all.filter((l) => {
                const m = parseListMetadata(l.metadata);
                return String(m.organization || '') === String(key);
            });
            const chSorted = ch.sort(
                (a, b) =>
                    new Date(b.created) - new Date(a.created)
            );
            childrenByOrgKey.set(key, chSorted);
            chSorted.forEach((c) => used.add(c.id));
        });

        const ungrouped = all
            .filter((l) => !used.has(l.id))
            .sort(
                (a, b) =>
                    new Date(b.created) - new Date(a.created)
            );

        const matchesSearch = (list) =>
            !q || (list.name || '').toLowerCase().includes(q);

        const visibleParents = parents.filter(({ key, label }) => {
            const ch = childrenByOrgKey.get(key) || [];
            const chHit = ch.some(matchesSearch);
            const orgHit =
                !q || (label || '').toLowerCase().includes(q);
            return orgHit || chHit;
        });

        const visibleUngrouped = ungrouped.filter(matchesSearch);

        return { visibleParents, childrenByOrgKey, visibleUngrouped };
    }, [lists, organizeByOrganization, q]);

    if (organizeByOrganization && treeModel) {
        const { visibleParents, childrenByOrgKey, visibleUngrouped } =
            treeModel;
        const totalRows =
            visibleParents.length + visibleUngrouped.length;

        return (
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            Lists ({totalRows > 0 ? totalRows : 0})
                        </TableCell>
                        <TableCell># Employees</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleParents.map((parent) => {
                        const expandId = `org:${parent.key}`;
                        const children =
                            childrenByOrgKey.get(parent.key) || [];
                        const descIds = descendantListIdsForOrgKey(
                            parent.key,
                            lists
                        );
                        const selectedIn = descIds.filter((id) =>
                            selectedLists.includes(id)
                        );
                        const checked =
                            descIds.length > 0 &&
                            selectedIn.length === descIds.length;
                        const indeterminate =
                            selectedIn.length > 0 &&
                            selectedIn.length < descIds.length;
                        const expanded = expandedOrgIds.has(expandId);

                        return (
                            <Fragment key={expandId}>
                                <TableRow style={{ borderBottom: 0 }}>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            aria-expanded={expanded}
                                            aria-label="expand organization"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpanded(expandId);
                                            }}
                                        >
                                            {expanded ? (
                                                <ExpandMoreIcon />
                                            ) : (
                                                <ChevronRightIcon />
                                            )}
                                        </IconButton>
                                        <FormControlLabel
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                            control={
                                                <Checkbox
                                                    checked={checked}
                                                    indeterminate={indeterminate}
                                                    color="primary"
                                                    disabled={
                                                        descIds.length ===
                                                            0 ||
                                                        (ongoing &&
                                                            descIds.length >
                                                                0 &&
                                                            descIds.every(
                                                                (id) =>
                                                                    previousEmployees &&
                                                                    previousEmployees.includes(
                                                                        id
                                                                    )
                                                            ))
                                                    }
                                                    onChange={() => {
                                                        const shouldSelect =
                                                            !checked;
                                                        setBulkSelection(
                                                            onUserListSelected,
                                                            selectedLists,
                                                            descIds,
                                                            shouldSelect
                                                        );
                                                    }}
                                                />
                                            }
                                            label={parent.label}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {children.reduce(
                                            (acc, c) =>
                                                acc + (c.usersCount || 0),
                                            0
                                        )}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        sx={{
                                            py: 0,
                                            borderBottom: 0,
                                        }}
                                    >
                                        <Collapse
                                            in={expanded}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <Table size="small">
                                                <TableBody>
                                                    {children.map((list) => (
                                                        <Fragment
                                                            key={list.id}
                                                        >
                                                            <TableRow
                                                                onClick={() =>
                                                                    onUserListSelected(
                                                                        list.id
                                                                    )
                                                                }
                                                            >
                                                                <TableCell
                                                                    sx={{
                                                                        pl: 6,
                                                                    }}
                                                                >
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={selectedLists.includes(
                                                                                    list.id
                                                                                )}
                                                                                name={
                                                                                    'checkbox_' +
                                                                                    list.id
                                                                                }
                                                                                color="primary"
                                                                                id={
                                                                                    'checkbox_' +
                                                                                    list.id
                                                                                }
                                                                                disabled={
                                                                                    ongoing &&
                                                                                    previousEmployees.includes(
                                                                                        list.id
                                                                                    )
                                                                                }
                                                                            />
                                                                        }
                                                                        label={
                                                                            list.name
                                                                        }
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    {
                                                                        list.usersCount
                                                                    }
                                                                </TableCell>
                                                                <TableCell>
                                                                    {list.usersCount >
                                                                        0 && (
                                                                        <ThemedLink
                                                                            className="show-recipients-link"
                                                                            onClick={(
                                                                                e
                                                                            ) =>
                                                                                toggleRecipients(
                                                                                    list.id,
                                                                                    e
                                                                                )
                                                                            }
                                                                        >
                                                                            {recipientsShown.includes(
                                                                                list.id
                                                                            )
                                                                                ? 'Hide'
                                                                                : 'Show'}{' '}
                                                                            recipients
                                                                        </ThemedLink>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            {recipientsShown.includes(
                                                                list.id
                                                            ) && (
                                                                <TableRow>
                                                                    <TableCell
                                                                        colSpan={
                                                                            3
                                                                        }
                                                                        sx={{
                                                                            py: 0,
                                                                            pl: 6,
                                                                        }}
                                                                    >
                                                                        <Collapse
                                                                            in={recipientsShown.includes(
                                                                                list.id
                                                                            )}
                                                                            timeout="auto"
                                                                            unmountOnExit
                                                                        >
                                                                            <div className="show-recipients">
                                                                                {loadingUsers.includes(
                                                                                    list.id
                                                                                ) ? (
                                                                                    <CircularProgress
                                                                                        size={
                                                                                            25
                                                                                        }
                                                                                    />
                                                                                ) : (
                                                                                    list.users &&
                                                                                    list
                                                                                        .users
                                                                                        .users &&
                                                                                    list.users.users.map(
                                                                                        (
                                                                                            u
                                                                                        ) => (
                                                                                            <div
                                                                                                key={
                                                                                                    u.email
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    u.email
                                                                                                }
                                                                                            </div>
                                                                                        )
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </Collapse>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </Fragment>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            </Fragment>
                        );
                    })}
                    {visibleUngrouped.map((list) => (
                        <Fragment key={list.id}>
                            <TableRow
                                onClick={() =>
                                    onUserListSelected(list.id)
                                }
                                style={{ borderBottom: 0 }}
                            >
                                <TableCell>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedLists.includes(
                                                    list.id
                                                )}
                                                name={'checkbox_' + list.id}
                                                color="primary"
                                                id={'checkbox_' + list.id}
                                                disabled={
                                                    ongoing &&
                                                    previousEmployees &&
                                                    previousEmployees.includes(
                                                        list.id
                                                    )
                                                }
                                            />
                                        }
                                        label={list.name}
                                    />
                                </TableCell>
                                <TableCell>{list.usersCount}</TableCell>
                                {list.usersCount > 0 && (
                                    <TableCell>
                                        <ThemedLink
                                            className="show-recipients-link"
                                            onClick={(e) =>
                                                toggleRecipients(
                                                    list.id,
                                                    e
                                                )
                                            }
                                        >
                                            {recipientsShown.includes(
                                                list.id
                                            )
                                                ? 'Hide'
                                                : 'Show'}{' '}
                                            recipients
                                        </ThemedLink>
                                    </TableCell>
                                )}
                            </TableRow>
                            {recipientsShown.includes(list.id) && (
                                <Collapse
                                    in={recipientsShown.includes(list.id)}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <div className="show-recipients">
                                        {loadingUsers.includes(list.id) ? (
                                            <CircularProgress size={25} />
                                        ) : (
                                            list.users &&
                                            list.users.users &&
                                            list.users.users.map((u) => (
                                                <div key={u.email}>
                                                    {u.email}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Collapse>
                            )}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        );
    }

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Lists ({searchedList.length})</TableCell>
                    <TableCell># Employees</TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {lists &&
                    lists.length > 0 &&
                    searchedList.map((list) => (
                        <Fragment key={list.id}>
                            <TableRow
                                onClick={() => {
                                    onUserListSelected(list.id);
                                }}
                                style={{ borderBottom: 0 }}
                            >
                                <TableCell>
                                    <Fragment>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedLists.includes(
                                                        list.id
                                                    )}
                                                    name={'checkbox_' + list.id}
                                                    color="primary"
                                                    id={'checkbox_' + list.id}
                                                    disabled={
                                                        ongoing &&
                                                        previousEmployees.includes(
                                                            list.id
                                                        )
                                                    }
                                                />
                                            }
                                        />
                                        {list.name}
                                    </Fragment>
                                </TableCell>
                                <TableCell>{list.usersCount}</TableCell>
                                {list.usersCount > 0 && (
                                    <TableCell>
                                        <ThemedLink
                                            className="show-recipients-link"
                                            onClick={(e) =>
                                                toggleRecipients(list.id, e)
                                            }
                                        >
                                            {recipientsShown.includes(list.id)
                                                ? 'Hide'
                                                : 'Show'}{' '}
                                            recipients
                                        </ThemedLink>
                                    </TableCell>
                                )}
                            </TableRow>
                            {recipientsShown.includes(list.id) && (
                                <Collapse
                                    in={recipientsShown.includes(list.id)}
                                    timeout="auto"
                                    unmountOnExit
                                >
                                    <div className="show-recipients">
                                        {loadingUsers.includes(list.id) ? (
                                            <CircularProgress size={25} />
                                        ) : (
                                            list.users &&
                                            list.users.users &&
                                            list.users.users.map((u) => (
                                                <div key={u.email}>
                                                    {u.email}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Collapse>
                            )}
                        </Fragment>
                    ))}
            </TableBody>
        </Table>
    );
}

export default observer(SelectUsersTable);
